import { useAppDispatch, useAppSelector } from "@/redux/hook";
import { useCallback, useLayoutEffect, useMemo } from "react";
import moment from "moment";
import { FetchReferrerTransactions } from "@/redux/features/actions/referrers";
import { ColumnDef } from "@tanstack/react-table";
import DataTable from "@/components/table2";
import { getDataType } from "@/components/table2/types";
import Currency from "@/components/etc/currency";

export default function ReferrerTransactions() {
  const user = useAppSelector((s) => s.user.user);
  const referrerId = user?.id;
  const dispatch = useAppDispatch();
  const { transactions }: any = useAppSelector((s) => s.referers);

  console.log(transactions);

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
          {
        accessorKey: "phone_number",
        header: "Phone Number",
        cell: ({ row }) => {
          const value = row.getValue("phone_number");
          if (!value) return null;
          const masked = String(value).replace(/.(?=.{3})/g, "*");
          return <span>{masked}</span>;
        },
      },
      {
        accessorFn: (row) => row?.amount,
        header: "Amount",
        cell: ({ getValue }) => `MWK ${getValue()}`,
      },
      {
        accessorFn: (row) => row?.discount_code,
        header: "Discount Code",
      },
      {
        accessorFn: (row) => row?.discount_type,
        header: "Discount Type",
      },
      {
        accessorFn: (row) => row?.final_amount,
        header: "Final Amount",
        cell: ({ getValue }) => `MWK ${getValue()}`,
      },
      {
        accessorFn: (row) => row?.status,
        header: "Status",
        cell: ({ getValue }) => {
          const value = getValue() as string;
          return (
            <span
              className={`${
                value?.toLowerCase() === "success"
                  ? "text-green-600"
                  : value?.toLowerCase() === "pending"
                    ? "text-orange-500"
                    : "text-red-600"
              }`}
            >
              {value?.toLowerCase()}
            </span>
          );
        },
      },
      {
        accessorFn: (row) => row?.telco,
        header: "Telco",
      },
      {
        accessorFn: (row) => row?.created_at,
        header: "Created At",
        cell: ({ getValue }) => moment(getValue() as string).format("LLL"),
      },
    ],
    [],
  );

const getData = useCallback(
  async ({ options, type }: Partial<getDataType> = {}) => {
    try {
      // Common params setup
      const baseParams = {
        ...options?.filters,
        pageNumber: (options?.pagination?.pageIndex || 0) + 1, // Convert to 1-based
      };

      if (type === "export") {
        // For exports, use total as pageSize to get all records
        const exportParams = {
          ...baseParams,
          pageSize: options?.pagination?.total || 1000, // Fallback if total not available
        };

        const exportResponse = await dispatch(
          FetchReferrerTransactions({ 
            referrerId, 
            ...exportParams 
          })
        ).unwrap();

        // Return just the transactions array for exports
        return exportResponse.list?.transactions || [];
      }

      // For regular paginated requests
      const regularParams = {
        ...baseParams,
        pageSize: options?.pagination?.pageSize || 10,
      };

      const response = await dispatch(
        FetchReferrerTransactions({ 
          referrerId, 
          ...regularParams 
        })
      ).unwrap();

      // Return structured pagination data for regular requests
      return response.list.transactions || []
    } catch (error: any) {
      console.error("Error fetching transactions:", error);
      return type === "export" 
        ? [] 
        : {
            data: [],
            total: 0,
            pageCount: 0,
            pageIndex: 0,
            pageSize: 10,
          };
    }
  },
  [dispatch, referrerId]
);
  useLayoutEffect(() => {
    getData({});
  }, [getData]);

  // const onFilter = useCallback(
  //   (filters: any) => {
  //     const data = {
  //       ...filters,
  //       to: !filters?.to
  //         ? ""
  //         : moment(filters?.to)
  //             .add(moment(filters?.to).format("HH") === "23" ? 0 : 86369, "seconds")
  //             .format("YYYY-MM-DD HH:mm"),
  //     };

  //     getData(data);
  //   },
  //   [getData]
  // );

  // const more = useCallback(
  //   (number?: number) => {
  //     getData({ ...filters, pageNumber: (transactions?.currentPage || 0) + (number || 1) });
  //   },
  //   [filters, getData, transactions?.currentPage]
  // );
  console.log(transactions);

  return (
    <div className="p-4">
      <div className="mb-4">
         <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Total Transactions Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Total Transactions</p>
              <h3 className="text-2xl font-bold text-gray-900">
                {transactions?.list?.summary?.totalTransactions || 0}
              </h3>
            </div>
            <div className="bg-blue-100 p-3 rounded-full">
              <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
          </div>
        </div>

        {/* Total Amount Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Total Amount</p>
              <h3 className="text-2xl font-bold text-gray-900">
                <Currency amount={(transactions?.list?.summary?.totalAmount ?? 0)} />
              </h3>
            </div>
            <div className="bg-green-100 p-3 rounded-full">
              <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </div>

        {/* Total Commission Card */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm font-medium">Your Balance</p>
              <h3 className="text-2xl font-bold text-gray-900">
                <Currency amount={(transactions?.list?.summary?.totalCommission ?? 0)} />
              </h3>
            </div>
            <div className="bg-purple-100 p-3 rounded-full">
              <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
              </svg>
            </div>
          </div>
        </div>
      </div>
      </div>

      <DataTable<any>
        title="Referrer Transactions"
        columns={columns}
        data={transactions?.list?.transactions ?? []}
        getData={getData}
        customPagination
        pagination={{
          totalPages: transactions?.total,
          pageNumber: (transactions?.currentPage || 1) - 1,
          pageSize: transactions?.pageSize,
          total: transactions?.total,
        }}
        // totalResults={transactions?.total}
        allowExport
        // minimizeFilter
        // hidePageSizeSelector
        // getMore={more}
        // onFilter={onFilter}
        // customPaginationActions
        // hideSearch
        // currentPage={transactions?.currentPage}
      />
    </div>
  );
}
